import {
  type SubNavRoute,
  ROUTE_DISCOVER_INVESTING_IDEAS,
  ROUTE_DISCOVER_INVESTING_IDEAS_MARKET,
  ROUTE_CREATE_CRITERIA,
  ROUTE_STOCKS_HOME,
  ROUTE_MARKET_HOME,
  ROUTE_STOCKS_MARKET,
  ROUTE_MARKET,
} from '@/constants/routes';
import { OneColumn } from '@components/Layout';
import { Column, TabList } from '@simplywallst/ui-core';
import ScreenerOutline from '@simplywallst/ui-core/icons/ScreenerOutline';
import { RouterLink } from '@redux-first-router-link';
import { DesktopSubNav, CTA, TabLink } from './styled';
import Media from 'react-media';
import { useTheme } from '@simplywallst/ui-core';
import { useUserAgent } from '@simplywallst/hooks/useUserAgent';
import { useTracking } from 'react-tracking';
import MobileTopbarSubNav, { ListItem } from '@/components/MobileTopbarSubNav';
import useUser from '@/hooks/useUser';
import type { ReactNode } from 'react';
import type { PrimaryCountryISOElseGlobal } from '@/constants/countries';
import { getTabLinkTo } from './utils';

export interface Props {
  route: string;
  market?: PrimaryCountryISOElseGlobal;
  children: ReactNode;
}

type DiscoverRoute = SubNavRoute & {
  to: string;
  toSubMarket: string;
};

const discoverRoutes: DiscoverRoute[] = [
  {
    to: ROUTE_DISCOVER_INVESTING_IDEAS,
    toSubMarket: ROUTE_DISCOVER_INVESTING_IDEAS_MARKET,
    label: 'Investing Ideas',
  },
  {
    to: ROUTE_STOCKS_HOME,
    toSubMarket: ROUTE_STOCKS_MARKET,
    label: 'Browse All Stocks',
  },
  {
    to: ROUTE_MARKET_HOME,
    toSubMarket: ROUTE_MARKET,
    label: 'Markets',
  },
];

export const ScreenerCallToAction = () => (
  <CTA>
    Have your own idea? Try our{' '}
    <span className="whitespace-nowrap">
      <RouterLink inheritColor to={{ type: ROUTE_CREATE_CRITERIA }}>
        Stock Screener
      </RouterLink>
      <ScreenerOutline />
    </span>
  </CTA>
);

export const DiscoverTabs = ({ route, market, children }: Props) => {
  const { isDesktop, isMobile, isTablet } = useUserAgent();
  const theme = useTheme();
  const user = useUser();
  const { trackEvent } = useTracking();

  return (
    <OneColumn>
      <Media query={{ minWidth: theme.lgMinRaw }} defaultMatches={isDesktop}>
        <DesktopSubNav alignItem="center" justifyContent="center">
          <TabList templateColumns="repeat(4, max-content)">
            {discoverRoutes.map(({ to, label, toSubMarket }, i) => (
              <TabLink
                key={`tab-${to}-${i}`}
                inheritColor
                noUnderline
                data-cy-id={`nav-${i}`}
                selected={[to, toSubMarket].includes(route)}
                to={getTabLinkTo({
                  to,
                  toSubMarket,
                  market,
                  isLoggedIn: user.data.loggedIn,
                })}
                onClick={() =>
                  trackEvent({
                    type: 'track',
                    subject: 'subNav',
                    modifier: typeof label === 'string' ? label : undefined,
                    action: 'click',
                  })
                }
              >
                {label}
              </TabLink>
            ))}
          </TabList>
          <Column alignItem="flex-end" justifyContent="center" fillVoid>
            <ScreenerCallToAction />
          </Column>
        </DesktopSubNav>
      </Media>
      <Media
        query={{ maxWidth: theme.mdMaxRaw }}
        defaultMatches={isMobile || isTablet}
      >
        <MobileTopbarSubNav routes={discoverRoutes} selected={route}>
          {({ routes, handleClick, refMap }) =>
            routes.map(({ to = '', toSubMarket = '', label, tracking }, i) => (
              <ListItem
                ref={refMap[i]}
                key={`MobileTopbarListItem-${to}-${i}`}
                selected={[to, toSubMarket].includes(route)}
              >
                <RouterLink
                  inheritColor
                  noUnderline
                  to={getTabLinkTo({
                    to,
                    toSubMarket,
                    market,
                    isLoggedIn: user.data.loggedIn,
                  })}
                  onClick={() => handleClick(tracking || label, to)}
                >
                  {label}
                </RouterLink>
              </ListItem>
            ))
          }
        </MobileTopbarSubNav>
      </Media>
      {children}
    </OneColumn>
  );
};
